/* The Capital Structure / theme.js
   Vanilla JS only. Every behaviour degrades gracefully without it. */
(function () {
  'use strict';

  var config = window.CapitalStructure || {};
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isMobile = window.matchMedia('(max-width: 700px)').matches;
  var motionOK = config.animations !== false && !reduced;
  var lightMotion = motionOK && !(isMobile && config.mobileSimplified);

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    var items = document.querySelectorAll('[data-reveal], [data-reveal-line]');
    if (!items.length) return;
    if (!motionOK || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    items.forEach(function (el, i) {
      if (!el.style.getPropertyValue('--reveal-delay')) {
        var group = el.closest('[data-reveal-group]');
        if (group) {
          var index = Array.prototype.indexOf.call(group.querySelectorAll('[data-reveal]'), el);
          el.style.setProperty('--reveal-delay', Math.max(0, index) * 80 + 'ms');
        }
      }
      io.observe(el);
    });
  }

  /* ---------- Header ---------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var threshold = 24;
    var onScroll = function () {
      header.classList.toggle('is-stuck', window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    var toggle = document.querySelector('[data-drawer-open]');
    var drawer = document.querySelector('[data-drawer]');
    if (!toggle || !drawer) return;
    var closers = drawer.querySelectorAll('[data-drawer-close]');
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('a, button');
      if (first) first.focus();
      document.addEventListener('keydown', onKey);
    }
    function close() {
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (lastFocus) lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
      if (e.key !== 'Tab') return;
      var focusables = drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    toggle.addEventListener('click', function () {
      drawer.classList.contains('is-open') ? close() : open();
    });
    closers.forEach(function (el) { el.addEventListener('click', close); });
  }

  /* ---------- Hero parallax and reveal ---------- */
  function initHero() {
    var hero = document.querySelector('[data-hero]');
    if (!hero) return;
    requestAnimationFrame(function () { hero.classList.add('is-ready'); });
    if (!lightMotion || !hero.hasAttribute('data-hero-parallax')) return;

    var layers = hero.querySelectorAll('[data-depth]');
    var targetX = 0, targetY = 0, currentX = 0, currentY = 0, raf = null;

    function loop() {
      currentX += (targetX - currentX) * 0.06;
      currentY += (targetY - currentY) * 0.06;
      layers.forEach(function (layer) {
        var depth = parseFloat(layer.getAttribute('data-depth')) || 0;
        layer.style.transform = 'translate3d(' + (currentX * depth) + 'px,' + (currentY * depth) + 'px,0)';
      });
      raf = Math.abs(targetX - currentX) > 0.1 || Math.abs(targetY - currentY) > 0.1
        ? requestAnimationFrame(loop) : null;
    }
    hero.addEventListener('pointermove', function (e) {
      var rect = hero.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 34;
      targetY = ((e.clientY - rect.top) / rect.height - 0.5) * 22;
      if (!raf) raf = requestAnimationFrame(loop);
    });
    hero.addEventListener('pointerleave', function () {
      targetX = 0; targetY = 0;
      if (!raf) raf = requestAnimationFrame(loop);
    });
  }

  /* ---------- Accordions: vault, blueprint steps, FAQ ---------- */
  function initAccordions() {
    document.querySelectorAll('[data-accordion]').forEach(function (root) {
      var single = root.hasAttribute('data-accordion-single');
      root.querySelectorAll('[data-accordion-item]').forEach(function (item) {
        var btn = item.querySelector('[data-accordion-btn]');
        var panel = item.querySelector('[data-accordion-panel]');
        if (!btn || !panel) return;
        btn.addEventListener('click', function () {
          var isOpen = item.classList.contains('is-open');
          if (single) {
            root.querySelectorAll('[data-accordion-item].is-open').forEach(function (other) {
              if (other === item) return;
              other.classList.remove('is-open');
              var b = other.querySelector('[data-accordion-btn]');
              var p = other.querySelector('[data-accordion-panel]');
              if (b) b.setAttribute('aria-expanded', 'false');
              if (p) p.setAttribute('hidden', '');
            });
          }
          item.classList.toggle('is-open', !isOpen);
          btn.setAttribute('aria-expanded', String(!isOpen));
          if (isOpen) { panel.setAttribute('hidden', ''); }
          else { panel.removeAttribute('hidden'); }
          var vault = root.closest('[data-vault]');
          if (vault) vault.classList.toggle('is-turning', !!root.querySelector('.is-open'));
        });
      });
    });
  }

  /* ---------- Comparison table hover highlight ---------- */
  function initCompare() {
    var table = document.querySelector('[data-compare-table]');
    if (!table) return;
    var heads = table.querySelectorAll('thead th[data-col]');
    function setActive(index, on) {
      table.querySelectorAll('[data-col="' + index + '"]').forEach(function (cell) {
        cell.classList.toggle('is-active', on);
      });
    }
    heads.forEach(function (th) {
      var index = th.getAttribute('data-col');
      th.addEventListener('mouseenter', function () { setActive(index, true); });
      th.addEventListener('mouseleave', function () { setActive(index, false); });
    });
    table.querySelectorAll('td[data-col]').forEach(function (td) {
      var index = td.getAttribute('data-col');
      td.addEventListener('mouseenter', function () { setActive(index, true); });
      td.addEventListener('mouseleave', function () { setActive(index, false); });
    });
  }

  /* ---------- Knowledge floors scroll spy ---------- */
  function initFloors() {
    var rail = document.querySelector('[data-floor-rail]');
    if (!rail || !('IntersectionObserver' in window)) return;
    var links = rail.querySelectorAll('a[href^="#"]');
    var floors = document.querySelectorAll('[data-floor]');
    if (!floors.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var id = entry.target.id;
        links.forEach(function (link) {
          link.classList.toggle('is-active', link.getAttribute('href') === '#' + id);
        });
      });
    }, { rootMargin: '-45% 0px -45% 0px' });
    floors.forEach(function (floor) { io.observe(floor); });
  }

  /* ---------- Article: reading bar, table of contents ---------- */
  function initArticle() {
    var body = document.querySelector('[data-article-body]');
    var bar = document.querySelector('[data-reading-bar]');
    if (bar && body) {
      var onScroll = function () {
        var rect = body.getBoundingClientRect();
        var total = rect.height - window.innerHeight;
        var progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
        bar.style.width = (progress * 100).toFixed(2) + '%';
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }

    var toc = document.querySelector('[data-toc]');
    if (!toc || !body) return;
    var headings = body.querySelectorAll('h2, h3');
    if (!headings.length) { toc.style.display = 'none'; return; }
    var list = document.createElement('ol');
    headings.forEach(function (heading, i) {
      if (!heading.id) heading.id = 'section-' + (i + 1);
      var li = document.createElement('li');
      li.className = heading.tagName === 'H3' ? 'lvl-3' : 'lvl-2';
      var a = document.createElement('a');
      a.href = '#' + heading.id;
      a.textContent = heading.textContent;
      li.appendChild(a);
      list.appendChild(li);
    });
    toc.appendChild(list);

    if ('IntersectionObserver' in window) {
      var tocLinks = toc.querySelectorAll('a');
      var spy = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          tocLinks.forEach(function (link) {
            link.classList.toggle('is-active', link.getAttribute('href') === '#' + entry.target.id);
          });
        });
      }, { rootMargin: '-15% 0px -70% 0px' });
      headings.forEach(function (h) { spy.observe(h); });
    }
  }

  /* ---------- Share ---------- */
  function initShare() {
    var btn = document.querySelector('[data-share-native]');
    if (!btn) return;
    if (!navigator.share) { btn.style.display = 'none'; return; }
    btn.addEventListener('click', function () {
      navigator.share({ title: document.title, url: window.location.href }).catch(function () {});
    });
    var copy = document.querySelector('[data-share-copy]');
    if (copy && navigator.clipboard) {
      copy.addEventListener('click', function () {
        navigator.clipboard.writeText(window.location.href).then(function () {
          var original = copy.querySelector('span').textContent;
          copy.querySelector('span').textContent = 'Link copied';
          setTimeout(function () { copy.querySelector('span').textContent = original; }, 2200);
        });
      });
    }
  }

  /* ---------- Ticker: duplicate track for a seamless loop ---------- */
  function initTicker() {
    var ticker = document.querySelector('[data-ticker-scroll]');
    if (!ticker || !motionOK) return;
    var track = ticker.querySelector('.ticker__track');
    if (!track || track.dataset.cloned === 'true') return;
    track.innerHTML = track.innerHTML + track.innerHTML;
    track.dataset.cloned = 'true';
  }

  /* ---------- Back to top ---------- */
  function initBackToTop() {
    var btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    var onScroll = function () {
      btn.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.9);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });
  }

  /* ---------- Checklist tick animation ---------- */
  function initChecklist() {
    var docs = document.querySelectorAll('.doc');
    if (!docs.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });
    docs.forEach(function (d) { io.observe(d); });
  }

  ready(function () {
    initHeader();
    initHero();
    initReveal();
    initAccordions();
    initCompare();
    initFloors();
    initArticle();
    initShare();
    initTicker();
    initBackToTop();
    initChecklist();
  });

  /* Shopify theme editor support */
  document.addEventListener('shopify:section:load', function () {
    initHeader(); initHero(); initReveal(); initAccordions();
    initCompare(); initFloors(); initArticle(); initTicker(); initChecklist();
  });
})();
